import { useState } from 'react';
import { Link } from 'react-router-dom';
import { services, stats, advantages, cases, faqData } from '@/mocks/data';

function HeroSection() {
  return (
    <header className="relative w-full h-[520px] md:h-[640px] overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20abstract%20digital%20network%20landscape%2C%20flowing%20geometric%20lines%20and%20nodes%20in%20warm%20coral%20and%20teal%20gradients%2C%20clean%20soft%20shapes%2C%20professional%20technology%20atmosphere%2C%20diffused%20warm%20lighting%2C%20editorial%20quality%2C%20no%20text&width=1600&height=900&seq=hero-bg&orientation=landscape"
        alt="91.cn 一站式互联网服务平台"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/50"></div>
      <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-4 md:px-6 text-center">
        <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-4 md:mb-6 leading-tight">
          一站式互联网服务<br />助力企业数字化转型
        </h1>
        <p className="text-sm md:text-base text-white/80 max-w-xl md:max-w-2xl mb-8 md:mb-10 leading-relaxed">
          91.cn 为您提供域名注册、网站建设、云主机、企业邮箱等全方位数字化解决方案。<br className="hidden md:block" />
          已服务超过 <strong>10万+</strong> 企业客户，99.9% 服务可用性保障。
        </p>
        <div className="flex flex-col sm:flex-row items-center gap-3">
          <Link
            to="/category/domain-registration"
            className="bg-primary-500 hover:bg-primary-600 text-background-50 px-8 py-3 rounded-md text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors"
          >
            立即开始
          </Link>
          <Link
            to="/about"
            className="border border-white/40 hover:border-white/80 text-white px-8 py-3 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
          >
            了解更多
          </Link>
        </div>
      </div>
    </header>
  );
}

function ServiceCard({ service, index }: { service: typeof services[0]; index: number }) {
  return (
    <Link
      to={`/category/${service.slug}`}
      className="group bg-background-50 rounded-2xl p-6 md:p-8 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer flex flex-col items-start gap-4"
    >
      <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-primary-100 flex items-center justify-center group-hover:bg-primary-500 transition-colors duration-300">
        <i className={`${service.icon} text-xl md:text-2xl text-primary-600 group-hover:text-background-50 transition-colors duration-300`}></i>
      </div>
      <h3 className="text-lg md:text-xl font-semibold text-foreground-900">{service.name}</h3>
      <p className="text-sm text-foreground-600 leading-relaxed">{service.description}</p>
      <span className="text-sm font-medium text-primary-600 group-hover:text-primary-700 flex items-center gap-1 mt-auto whitespace-nowrap">
        了解更多 <i className="ri-arrow-right-line text-xs"></i>
      </span>
    </Link>
  );
}

function ServicesSection() {
  return (
    <section className="w-full px-4 md:px-6 lg:px-10 py-16 md:py-20 bg-background-100">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12 md:mb-14">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 mb-3">
            全方位互联网服务
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-lg mx-auto">
            从域名到云端，从建站到邮箱，一站式满足企业数字化全部需求
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {services.map((service, i) => (
            <ServiceCard key={service.slug} service={service} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function StatsSection() {
  return (
    <section className="w-full px-4 md:px-6 lg:px-10 py-12 md:py-16 bg-primary-500">
      <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
        {stats.map((stat) => (
          <div key={stat.label} className="text-center">
            <div className="text-3xl md:text-4xl font-heading font-bold text-background-50 mb-1">
              {stat.value}
            </div>
            <div className="text-sm text-background-50/80">{stat.label}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function AdvantagesSection() {
  return (
    <section className="w-full px-4 md:px-6 lg:px-10 py-16 md:py-20 bg-background-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12 md:mb-14">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 mb-3">
            为什么选择 91.cn？
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-lg mx-auto">
            四大核心优势，为企业数字化保驾护航
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {advantages.map((adv) => (
            <div key={adv.title} className="text-center group">
              <div className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-4 rounded-2xl bg-accent-100 flex items-center justify-center group-hover:bg-accent-500 transition-colors duration-300">
                <i className={`${adv.icon} text-xl md:text-2xl text-accent-600 group-hover:text-background-50 transition-colors duration-300`}></i>
              </div>
              <h3 className="text-base md:text-lg font-semibold text-foreground-900 mb-2">{adv.title}</h3>
              <p className="text-sm text-foreground-600 leading-relaxed">{adv.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CasesSection() {
  return (
    <section className="w-full px-4 md:px-6 lg:px-10 py-16 md:py-20 bg-background-100">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12 md:mb-14">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 mb-3">
            客户成功案例
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-lg mx-auto">
            超过10万家企业信任 91.cn，听听他们的故事
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {cases.map((c) => (
            <div key={c.name} className="bg-background-50 rounded-2xl p-6 md:p-7 border border-background-200/70">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl overflow-hidden bg-background-100 flex-shrink-0">
                  <img src={c.logo} alt={c.name} className="w-full h-full object-cover" />
                </div>
                <div>
                  <h4 className="font-semibold text-foreground-900 text-sm">{c.name}</h4>
                  <p className="text-xs text-foreground-500">{c.industry}</p>
                </div>
              </div>
              <p className="text-sm text-foreground-600 leading-relaxed">{c.result}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="w-full px-4 md:px-6 lg:px-10 py-16 md:py-20 bg-background-50">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12 md:mb-14">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 mb-3">
            常见问题
          </h2>
          <p className="text-sm md:text-base text-foreground-600">
            关于 91.cn 服务的常见疑问解答
          </p>
        </div>
        <div className="flex flex-col gap-3">
          {faqData.map((faq, i) => (
            <div
              key={i}
              className="border border-background-200/70 rounded-xl overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === i ? null : i)}
                className="w-full flex items-center justify-between px-5 md:px-6 py-4 text-left cursor-pointer hover:bg-background-100 transition-colors"
              >
                <span className="text-sm md:text-base font-medium text-foreground-900 pr-4">
                  {faq.question}
                </span>
                <i
                  className={`ri-arrow-down-s-line text-lg text-foreground-500 flex-shrink-0 transition-transform duration-300 ${
                    openIndex === i ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>
              {openIndex === i && (
                <div className="px-5 md:px-6 pb-4">
                  <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="relative w-full px-4 md:px-6 lg:px-10 py-16 md:py-20 overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=Modern%20minimalist%20abstract%20digital%20wave%20pattern%2C%20flowing%20warm%20coral%20and%20teal%20gradient%20lines%2C%20clean%20soft%20geometric%20shapes%2C%20professional%20technology%20atmosphere%2C%20editorial%20quality%2C%20no%20text&width=1600&height=900&seq=cta-bg&orientation=landscape"
        alt="开始数字化之旅"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/40 to-black/60"></div>
      <div className="relative z-10 max-w-2xl mx-auto text-center">
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-white mb-4">
          准备好开启您的数字化之旅了吗？
        </h2>
        <p className="text-sm md:text-base text-white/80 mb-8 leading-relaxed">
          立即加入 91.cn，享受高效稳定的互联网服务，让您的企业在数字时代脱颖而出。
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/category/domain-registration"
            className="bg-primary-500 hover:bg-primary-600 text-background-50 px-8 py-3 rounded-md text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors"
          >
            免费咨询
          </Link>
          <a
            href="tel:400-810-9191"
            className="border border-white/40 hover:border-white/80 text-white px-8 py-3 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer transition-colors flex items-center gap-2"
          >
            <i className="ri-phone-line"></i> 400-810-9191
          </a>
        </div>
      </div>
    </section>
  );
}

export default function HomePage() {
  return (
    <main>
      <HeroSection />
      <ServicesSection />
      <StatsSection />
      <AdvantagesSection />
      <CasesSection />
      <FAQSection />
      <CTASection />
    </main>
  );
}